import SeverityBadge, { type SeverityLevel } from '@/components/UI/SeverityBadge/SeverityBadge';
import { _isNotBlank } from '@/littledash';
import type { ScorecardFindingV1, ScorecardSeverity } from '@/model/Agent.model';
import type { FC } from 'react';
import { RiArrowDownSLine } from 'react-icons/ri';
import styles from './ScorecardFinding.module.scss';

interface ScorecardFindingProps {
  finding: ScorecardFindingV1;
  /** Unique within the report; used for the detail region's DOM id. */
  id: string;
}

/** The scorecard grades urgency of researcher action, which maps onto the badge's visual scale. */
const severityLevel: Record<ScorecardSeverity, SeverityLevel> = {
  critical: 'high',
  warning: 'medium',
  info: 'low',
};

// A severity the UI does not recognise (server enum widened ahead of a UI deploy) is shown at the
// highest weight: under-stating the urgency of a welfare finding is the harmful direction.
const levelFor = (severity: ScorecardSeverity): SeverityLevel => severityLevel[severity] ?? 'high';

/**
 * One scorecard finding: a one-line headline that expands to the reasoning and the recommended
 * action. Collapsed by default so a reader scans the headlines first and opens only what they act on.
 *
 * Native `<details>` rather than a button with `useState`. Open/closed state, keyboard handling and
 * screen-reader semantics all come for free, and — the reason it matters here — the global `button`
 * rules in Button.scss do not apply, so a long headline wraps instead of being clipped by their
 * `white-space: nowrap`. Findings with no detail render as a plain row, since a disclosure that opens
 * to nothing is worse than none.
 */
const ScorecardFinding: FC<ScorecardFindingProps> = ({ finding, id }) => {
  const hasImpact = _isNotBlank(finding.impact);
  const hasRecommendation = _isNotBlank(finding.recommendation);
  const hasDetail = hasImpact || hasRecommendation;

  const headline = (
    <>
      <SeverityBadge level={levelFor(finding.severity)} label={finding.severity} className={styles.badge} />
      <span className={styles.headline} data-test-element="headline">
        {finding.finding}
      </span>
    </>
  );

  return (
    <li
      className={styles.finding}
      data-test-component="ScorecardFinding"
      data-test-key={finding.severity}
      data-testid={id}
    >
      {hasDetail ? (
        <details>
          <summary className={styles.header} data-test-element="header">
            {headline}
            <RiArrowDownSLine className={styles.chevron} aria-hidden="true" />
          </summary>
          <div id={`${id}__detail`} className={styles.detail} data-test-element="detail">
            {hasImpact ? (
              <p className={styles.detailBlock} data-test-element="why-this-matters">
                <span className={styles.detailLabel}>Why this matters</span>
                {finding.impact}
              </p>
            ) : null}
            {hasRecommendation ? (
              <p className={styles.detailBlock} data-test-element="recommendation">
                <span className={styles.detailLabel}>Recommendation</span>
                {finding.recommendation}
              </p>
            ) : null}
          </div>
        </details>
      ) : (
        <div className={styles.headerStatic}>{headline}</div>
      )}
    </li>
  );
};

export default ScorecardFinding;
